import { useState } from "react";

type ArtworkImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "onLoad"> & {
  /**
   * Pixel-art threshold. If the loaded image's natural width is <= this,
   * we switch to nearest-neighbor rendering so the upscaled bitmap stays
   * crisp instead of blurring at every edge. Defaults to 96 (covers 32x32
   * identicons, 64x64 sprites, etc).
   */
  pixelThreshold?: number;
};

/**
 * <img> wrapper that picks the right rendering filter based on image size:
 *   - small bitmaps (≤ pixelThreshold px on the longer side): `pixelated`
 *   - everything else (photos, large generative renders): browser default
 *
 * Use this anywhere we display NFT artwork — covers, banners, public mint
 * pages — so 32×32 PFPs stay sharp without making 1024×1024 pieces look
 * jaggy.
 */
export function ArtworkImage({ pixelThreshold = 96, style, ...rest }: ArtworkImageProps) {
  const [pixelated, setPixelated] = useState(false);
  return (
    <img
      {...rest}
      style={{
        ...style,
        imageRendering: pixelated ? "pixelated" : undefined,
      }}
      onLoad={(e) => {
        const img = e.currentTarget;
        const longest = Math.max(img.naturalWidth, img.naturalHeight);
        setPixelated(longest > 0 && longest <= pixelThreshold);
      }}
    />
  );
}
